import React from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '../../../shared/components';
import { getContacts } from '../../contacts/services/contactService';
import { Contact } from '../../contacts/types/Contact';
import { FALLBACK_CURRENCY, formatMoney, parseNumber } from '../../invoices/invoiceForm';
import { createPaymentLink, getPaymentSettings, sendPaymentLink } from '../services/paymentService';
import { Payment } from '../types/Payment';
import styles from '../Payments.module.css';

type CreatePaymentModalProps = {
  defaultAmount?: number;
  defaultCurrency?: string;
  defaultDescription?: string;
  contactId?: string;
  conversationId?: string;
  invoiceId?: string;
  lockAmount?: boolean;
  sendAfterCreate?: boolean;
  onClose: () => void;
  onCreated?: (payment: Payment) => void;
};

export const CreatePaymentModal: React.FC<CreatePaymentModalProps> = ({
  defaultAmount,
  defaultCurrency,
  defaultDescription,
  contactId: lockedContactId,
  conversationId,
  invoiceId,
  lockAmount,
  sendAfterCreate = false,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation(['payments', 'common']);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [amount, setAmount] = React.useState(defaultAmount != null ? defaultAmount.toFixed(2) : '');
  const [currency, setCurrency] = React.useState(defaultCurrency || FALLBACK_CURRENCY);
  const [description, setDescription] = React.useState(defaultDescription ?? '');
  const [contactId, setContactId] = React.useState(lockedContactId ?? '');
  const [expiresInMinutes, setExpiresInMinutes] = React.useState('60');
  const [sendNow, setSendNow] = React.useState(Boolean(sendAfterCreate && conversationId));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [created, setCreated] = React.useState<Payment | null>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [contactList, settings] = await Promise.all([
          lockedContactId ? Promise.resolve([] as Contact[]) : getContacts({ pageSize: 200 }).catch(() => [] as Contact[]),
          getPaymentSettings().catch(() => null),
        ]);
        if (cancelled) return;
        setContacts(contactList);
        if (!defaultCurrency && settings?.currency) {
          setCurrency(settings.currency);
        }
      } catch {
        // Contact list is optional for invoice/conversation-locked flows.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [defaultCurrency, lockedContactId]);

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const submit = async () => {
    const parsed = parseNumber(amount);
    if (!parsed || parsed <= 0) {
      setError(t('amountRequired'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const payment = await createPaymentLink({
        amount: parsed,
        currency,
        description: description.trim() || undefined,
        contactId: (lockedContactId || contactId) || undefined,
        conversationId,
        invoiceId,
        expiresInMinutes: Number(expiresInMinutes) || 60,
      });
      if (sendNow && (conversationId || payment.conversationId)) {
        const sent = await sendPaymentLink(payment.id, {
          conversationId: conversationId || payment.conversationId,
        });
        setCreated(sent);
        onCreated?.(sent);
      } else {
        setCreated(payment);
        onCreated?.(payment);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToCreate'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="create-payment-title" onClick={() => { if (!busy) onClose(); }}>
      <div className={styles.modalCard} onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 id="create-payment-title" className={styles.modalTitle}>
            {invoiceId ? t('payOnline') : conversationId ? t('requestPayment') : t('createPayment')}
          </h3>
          <button className={styles.modalClose} type="button" aria-label={t('close')} onClick={onClose} disabled={busy}>
            뿯½
          </button>
        </div>

        {invoiceId ? <p className={styles.hint}>{t('invoiceHint')}</p> : null}
        {conversationId && !invoiceId ? <p className={styles.hint}>{t('conversationOnlyHint')}</p> : null}
        {error ? <div className={styles.error}>{error}</div> : null}

        {created ? (
          <div className={styles.createdBox}>
            <strong>{sendNow ? t('linkSent') : t('createdTitle')}</strong>
            <p className={styles.hint}>{t('createdHint')}</p>
            {created.checkoutUrl ? (
              <>
                <span className={styles.createdUrl}>{created.checkoutUrl}</span>
                <div className={styles.actionsCell}>
                  <button className="btn btn-secondary" type="button" onClick={() => void copyUrl(created.checkoutUrl!)}>
                    {copied ? t('copied') : t('copyLink')}
                  </button>
                  <a className="btn btn-secondary" href={created.checkoutUrl} target="_blank" rel="noreferrer">
                    {t('checkoutUrl')}
                  </a>
                </div>
              </>
            ) : null}
            <button className="btn btn-primary" type="button" onClick={onClose}>{t('close')}</button>
          </div>
        ) : (
          <>
            <div className={styles.field}>
              <label className={styles.label}>{t('amount')}</label>
              <input
                inputMode="decimal"
                value={amount}
                disabled={busy || lockAmount}
                onChange={event => setAmount(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>{t('currency')}</label>
              <select value={currency} disabled={busy || Boolean(invoiceId)} onChange={event => setCurrency(event.target.value)}>
                {['AZN', 'USD', 'EUR'].map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>{t('description')}</label>
              <input value={description} disabled={busy} onChange={event => setDescription(event.target.value)} />
            </div>
            {!lockedContactId && !conversationId ? (
              <div className={styles.field}>
                <label className={styles.label}>{t('contact')}</label>
                <SearchableSelect
                  value={contactId}
                  onChange={setContactId}
                  options={contacts.map(contact => ({ value: contact.id, label: contact.name, meta: contact.email }))}
                  clearOption={{ value: '', label: t('noContact') }}
                />
              </div>
            ) : null}
            <div className={styles.field}>
              <label className={styles.label}>{t('expiresInMinutes')}</label>
              <input value={expiresInMinutes} disabled={busy} onChange={event => setExpiresInMinutes(event.target.value)} />
            </div>
            {conversationId ? (
              <label className={styles.checkboxRow}>
                <input type="checkbox" checked={sendNow} disabled={busy} onChange={event => setSendNow(event.target.checked)} />
                {t('sendAfterCreate')}
              </label>
            ) : null}
            <div className={styles.formActions}>
              <button className="btn btn-secondary" type="button" disabled={busy} onClick={onClose}>{t('close')}</button>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void submit()}>
                {sendNow ? t('sendLink') : t('create')}
              </button>
            </div>
            {amount ? <p className={styles.hint}>{formatMoney(parseNumber(amount) ?? 0, currency)}</p> : null}
          </>
        )}
      </div>
    </div>
  );
};
